import classNames from "classnames";
import React, { useState } from "react";
import { useHistory, useParams } from "react-router";
import { common_utils } from "../../../../../../../common/common_utils";
import {
	SeshTextChannelData,
	SyncDirection,
	syncDirectionToName,
	SyncInclusionType,
} from "../../../../../../../common/sesh_types";
import { SyncEntity } from "../../../../../../../src/entity/Sync";
import { DashboardUtils } from "../../../../dashboard_utils";
import { trpc } from "../../../../utils/trpc";
import GuildSettingsChannelPicker, {
	RequiredPermission,
} from "./guild_settings_channel_picker";
import GoogleCalendarPicker from "./google_calendar_picker";
import IncludedSeshEventsDropdown from "./included_sesh_events_dropdown";

export default function SyncDetails(props: {
	onBackClicked: Function;
	selectedSyncDirection: SyncDirection;
	editSync?: SyncEntity;
}) {
	const { server_id } = useParams<{ server_id: string }>();

	const utils = trpc.useContext();
	const history = useHistory();

	const onSuccess = async () => {
		await utils.gcal.getSyncsOrAuthURL.invalidate();
		history.push(common_utils.getGuildSettingsURL(server_id, "sync"));
	};

	const createSyncMutation = trpc.gcal.createSync.useMutation({
		onSuccess,
		onError: (e) => {
			DashboardUtils.ShowDefaultErrorAlert(e.message ?? "Error");
		},
	});

	const editSyncMutation = trpc.gcal.editSync.useMutation({
		onSuccess,
		onError: (e) => {
			DashboardUtils.ShowDefaultErrorAlert(e.message ?? "Error");
		},
	});

	const [included_sesh_events, set_included_sesh_events] =
		useState<SyncInclusionType | null>(
			props.editSync?.included_sesh_events ?? SyncInclusionType.All
		);

	const [included_channel, set_included_channel] = useState<string | null>(
		props.editSync?.included_channel
	);

	const [include_existing_events, set_include_existing_events] =
		useState(true);

	const [gcal_id, set_gcal_id] = useState<string | null>(
		props.editSync?.gcal_id
	);

	const [gcal_name, set_gcal_name] = useState<string | null>(
		props.editSync?.gcal_name
	);

	const [new_gcal_name, set_new_gcal_name] = useState<string | null>(null);

	const [output_channel, set_output_channel] = useState<string | null>(
		props.editSync?.output_channel
	);

	const [
		use_events_channel_as_output_channel,
		set_use_events_channel_as_output_channel,
	] = useState<boolean | null>(
		props.editSync?.use_events_channel_as_output_channel
	);

	function baseIsConfigurationValid() {
		switch (props.selectedSyncDirection) {
			case SyncDirection.Outbound:
				return (
					(included_sesh_events !== null ||
						included_sesh_events !== undefined) &&
					(included_sesh_events !== SyncInclusionType.Channel ||
						(included_channel !== null &&
							included_channel !== undefined)) &&
					gcal_id !== null &&
					gcal_id !== undefined
				);
			case SyncDirection.Inbound:
				return (
					gcal_id !== null &&
					gcal_id !== undefined &&
					output_channel !== null &&
					output_channel !== undefined
				);

			case SyncDirection.Bidirectional:
				return (
					(included_sesh_events !== null ||
						included_sesh_events !== undefined) &&
					(included_sesh_events !== SyncInclusionType.Channel ||
						(included_channel !== null &&
							included_channel !== undefined)) &&
					gcal_id !== null &&
					gcal_id !== undefined &&
					output_channel !== null &&
					output_channel !== undefined
				);
		}
	}

	function isConfigurationValid() {
		const isBaseValid = baseIsConfigurationValid();

		if (!isBaseValid) {
			return false;
		}

		if (!props.editSync) {
			return isBaseValid;
		}

		switch (props.selectedSyncDirection) {
			case SyncDirection.Outbound:
				return (
					included_sesh_events !==
						props.editSync.included_sesh_events ||
					included_channel !== props.editSync.included_channel ||
					gcal_id !== props.editSync.gcal_id
				);
			case SyncDirection.Inbound:
				return (
					gcal_id !== props.editSync.gcal_id ||
					output_channel !== props.editSync.output_channel
				);

			case SyncDirection.Bidirectional:
				return (
					included_sesh_events !==
						props.editSync.included_sesh_events ||
					included_channel !== props.editSync.included_channel ||
					gcal_id !== props.editSync.gcal_id ||
					output_channel !== props.editSync.output_channel
				);
		}
	}

	let settings: React.ReactElement;
	switch (props.selectedSyncDirection) {
		case SyncDirection.Outbound:
			settings = (
				<>
					<IncludedSeshEventsDropdown
						included_sesh_events={included_sesh_events}
						set_included_sesh_events={set_included_sesh_events}
						included_channel={included_channel}
						set_included_channel={set_included_channel}
						hasUnsavedChanges={
							props.editSync &&
							(props.editSync.included_sesh_events !==
								included_sesh_events ||
								props.editSync.included_channel !==
									included_channel)
						}
						include_existing_events={
							!props.editSync
								? include_existing_events
								: undefined
						}
						set_include_existing_events={
							!props.editSync
								? set_include_existing_events
								: undefined
						}
					/>

					<GoogleCalendarPicker
						syncType={props.selectedSyncDirection}
						gcal_id={gcal_id}
						set_gcal_id={set_gcal_id}
						set_gcal_name={set_gcal_name}
						hasUnsavedChanges={
							props.editSync && props.editSync.gcal_id !== gcal_id
						}
					/>
				</>
			);
			break;
		case SyncDirection.Inbound:
			settings = (
				<>
					<GoogleCalendarPicker
						syncType={props.selectedSyncDirection}
						gcal_id={gcal_id}
						set_gcal_id={set_gcal_id}
						set_gcal_name={set_gcal_name}
						hasUnsavedChanges={
							props.editSync && props.editSync.gcal_id !== gcal_id
						}
					/>
					<GuildSettingsChannelPicker
						channel={output_channel}
						set_channel={set_output_channel}
						name="Select output channel"
						description="Events created in Google Calendar will be sent to this channel"
						hasUnsavedChanges={
							props.editSync &&
							props.editSync.output_channel !== output_channel
						}
						requiredPermission={RequiredPermission.Write}
					/>
				</>
			);
			break;
		case SyncDirection.Bidirectional:
			settings = (
				<>
					<IncludedSeshEventsDropdown
						included_sesh_events={included_sesh_events}
						set_included_sesh_events={set_included_sesh_events}
						included_channel={included_channel}
						set_included_channel={set_included_channel}
						hasUnsavedChanges={
							props.editSync &&
							(props.editSync.included_sesh_events !==
								included_sesh_events ||
								props.editSync.included_channel !==
									included_channel)
						}
						include_existing_events={
							!props.editSync
								? include_existing_events
								: undefined
						}
						set_include_existing_events={
							!props.editSync
								? set_include_existing_events
								: undefined
						}
					/>

					<GoogleCalendarPicker
						syncType={props.selectedSyncDirection}
						gcal_id={gcal_id}
						set_gcal_id={set_gcal_id}
						set_gcal_name={set_gcal_name}
						hasUnsavedChanges={
							props.editSync && props.editSync.gcal_id !== gcal_id
						}
					/>

					<GuildSettingsChannelPicker
						channel={output_channel}
						set_channel={set_output_channel}
						name="Select output channel"
						description="Events created in Google Calendar will be sent to this channel"
						hasUnsavedChanges={
							props.editSync &&
							props.editSync.output_channel !== output_channel
						}
						requiredPermission={RequiredPermission.Write}
					/>
				</>
			);
			break;
	}

	return (
		<div className="flex flex-col gap-2">
			<h1 className="is-size-5 has-text-weight-bold has-text-centered">
				{syncDirectionToName(props.selectedSyncDirection)} sync{" "}
				{props.editSync ? "settings" : "setup"}
			</h1>
			<div className="flex flex-col gap-4">
				{settings}
				<button
					className={classNames("dashboard-primary-button button", {
						"is-loading":
							editSyncMutation.isLoading ||
							createSyncMutation.isLoading,
					})}
					disabled={!isConfigurationValid()}
					onClick={() => {
						if (!isConfigurationValid()) {
							return;
						}

						if (props.editSync) {
							editSyncMutation.mutate({
								id: props.editSync.id,
								data: {
									included_sesh_events,
									included_channel,
									gcal_id,
									gcal_name,
									output_channel,
									use_events_channel_as_output_channel,
								},
							});
						} else {
							createSyncMutation.mutate({
								guild_id: server_id,
								syncDirection: props.selectedSyncDirection,
								included_sesh_events,
								included_channel,
								include_existing_events,
								gcal_id,
								gcal_name,
								output_channel,
								use_events_channel_as_output_channel,
							});
						}
					}}
				>
					{props.editSync ? "Save changes" : "Finish sync setup"}
				</button>
			</div>
		</div>
	);
}
